import {Injectable} from '@angular/core';
import packageInfo from '../../package.json';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ErrorDialogComponent} from './dialogs/error-dialog/error-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {InfoDialogComponent} from './dialogs/info-dialog/info-dialog.component';
import {ConfirmDialogComponent} from './dialogs/confirm-dialog/confirm-dialog.component';
import { AgaveConfig, AgaveService, IAgaveError, Persistent } from 'tq-agave';
import { ConfirmDialogData } from './dialogs/confirm-dialog/confirm-dialog.types';
import { TqApp } from './components/tq-apps/tq-apps.types';

@Injectable({
    providedIn: 'root'
})
export class GlobalsService {

    public static IMTEAM_PREFIX = 'it.imteam.'; // TODO cambia!!

    static applicationCode = 'rms';

    // CONFIGURAZIONE - LA CONFIGURAZIONE ARRIVA DA public/jsp/config.jsp
    static agaveServiceUrl: string;

    static forgotPasswordUrl: string = '';
    static resetPasswordUrl: string = '';

    // eslint-disable-next-line max-len
    static agaveTqdocDownloadDocumentUrl = '/tqdocws/blob/fake.docx?_use_original_file_name=1&_action_extract_stream=1&_page=TQDOC_Documenti_Versioni_J&_state=find&_token={ticket}&id_documento={id_documento}';
    static lastErrorMessage: string;
    static agaveProxy: AgaveConfig[] = []; // anche tqdoc va qui dentro
    static tqApps: TqApp[] = [];
    static microsoftLoginLink: string = 'http://sviluppobase:8081/tqdoc';
    static isCloud: boolean = false;

    private static hack?: GlobalsService;

    constructor(
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _agaveService: AgaveService
    ) {
        GlobalsService.hack = this;
    }

    static get applicationVersion(): string {
        return packageInfo.version;
    }

    // questa funzione sarà usata come default per gestire gli errori che vengono dai persistent
    static errorHandler(err: IAgaveError | string) {
        if (!GlobalsService.hack) {
            return;
        }
        if (typeof err === 'string') {
            GlobalsService.hack.openErrorDialogString(err, 'generic-error', true);
        } else {
            GlobalsService.hack.openErrorDialog(err, 'generic-error', true);
        }
    }

    static openErrorDialog(
        error: IAgaveError,
        title: string,
        matDialog: MatDialog,
        force: boolean = false,
        checkIfNeedToLogin: boolean = true
    ): Promise<any> {
        return new Promise<any>(resolve => {
            if (this.lastErrorMessage == undefined || (error.agaveErrorCode !== 'D0' && error.agaveErrorCode !== this.lastErrorMessage) || force) {
                const errorDialogRef: MatDialogRef<ErrorDialogComponent> = matDialog.open(ErrorDialogComponent, {
                    panelClass: 'error-form-dialog',
                    data: {
                        title: title,
                        message: error.message,
                        armoniaErrorCode: error.agaveErrorCode,
                    }
                });
                this.lastErrorMessage = error.agaveErrorCode;
                if (checkIfNeedToLogin) {
                    errorDialogRef.componentInstance.checkIfNeedToLogin();
                }
                errorDialogRef.afterClosed().subscribe(value => {
                    this.lastErrorMessage = '';
                    resolve(value);
                });

            } else {
                resolve(true);
            }
        });
    }

    static openInfoDialog(title: string, message: string, dialog: MatDialog): Promise<boolean> {
        return new Promise<boolean>(
            (resolve, reject) => {
                try {
                    const dialogRef = dialog.open(InfoDialogComponent, {
                        panelClass: 'info-dialog',
                        data: { title, message },
                        disableClose: true
                    });
                    dialogRef.backdropClick().subscribe(value => {
                        value.preventDefault();
                    });
                    dialogRef.afterClosed().subscribe(result => {
                        resolve(result);
                    });
                } catch (e) {
                    reject(e);
                }
            }
        );
    }

    static openConfirmDialog(data: ConfirmDialogData, dialog: MatDialog): Promise<boolean> {
        return new Promise<any>((resolve, reject) => {
            try {
                const dialogRef = dialog.open(ConfirmDialogComponent, {
                    panelClass: 'info-dialog',
                    data,
                    disableClose: true
                });
                dialogRef.backdropClick().subscribe(value => {
                    value.preventDefault();
                });
                dialogRef.afterClosed().subscribe(result => {
                    resolve(result);
                });
            } catch (e) {
                reject(e);
            }
        });
    }

    static openErrorDialogString(errorDescription: string, title: string, matDialog: MatDialog, force: boolean = false): Promise<any> {
        return this.openErrorDialog({message: errorDescription} as IAgaveError, title, matDialog, force);
    }

    static openSnackBar(matSnackBar: MatSnackBar, message: string, action: string): void {
        matSnackBar.open(message, action, {
            duration: 5000,
            verticalPosition: 'bottom',
            horizontalPosition: 'end',
            panelClass: 'snack-bar-custom',
        });
    }

    static exportExcel(page: string, ticket: string, parametri: any = {}, operators: any = {}, fileName: string = 'CadmoDriver.xls', action: string = 'export_excel'): void {
        const params: any = {
            _page: page,
            _state: 'find',
            _token: ticket,
            ...parametri,
            ...operators
        };
        params['_action_' + action] = '1';
        let url = GlobalsService.agaveServiceUrl.replace('jsp/json.jsp', '/blob/' + fileName + '?');
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const element = params[key];
                if (element != null) {
                    if (element instanceof Array) {
                        url += key + '=' + encodeURIComponent(JSON.stringify(element)) + '&';
                    } else {
                        url += key + '=' + encodeURIComponent(element) + '&';
                    }
                }
            }
        }
        window.open(url, '_blank');
    }

    // queste sotto servono per i persisten (forse sarebbe meglio metterle direttamente in armonia in una classe di utilità)
    static getDateFromEpoch(epoch: any): Date {
        let date = null;
        try {
            if (epoch instanceof Date || epoch?._isAMomentObject) {
                date = epoch;
            } else if (epoch && !isNaN(epoch)) {
                date = new Date(epoch * 1000);
            } else if (epoch) {
                date = new Date(epoch);
            }
        } catch (error) {
            date = null;
        }
        return date;
    }

    static getEpochFromDate(date: any): number {
        let epoch = null;
        try {
            if (date?._isAMomentObject) {
                epoch = date.unix();
            } else if (date) {
                const date2 = new Date(date);
                epoch = Math.round(date2.getTime() / 1000);
            }
        } catch (error) {
            date = null;
        }
        return epoch;
    }

    static getTimeFromDate(data: Date): string | null {
        if (data) {
            return data.getHours().toString().padStart(2, '0') + ':' + data.getMinutes().toString().padStart(2, '0');
        } else {
            return null;
        }
    }

    static setTimeToDate(date: any, time: string): Date | null {
        if (date?._isAMomentObject) {
            date = date.toDate();
        }
        if (date && time && time.includes(':')) {
            date.setHours(+time.split(':')[0]);
            date.setMinutes(+time.split(':')[1]);
            return date;
        } else {
            return null;
        }
    }

    static getBooleanFrom01(s01: string | boolean): boolean | undefined {
        if (s01 && s01 === '1' || s01 === true) {
            return true;
        } else if (s01 && s01 === '0' || s01 === false) {
            return false;
        } else {
            return undefined;
        }
    }

    static get01FromBoolean(bool: boolean): string | undefined {
        if (bool === true) {
            return '1';
        } else if (bool === false) {
            return '0';
        } else {
            return undefined;
        }
    }

    getFullVersion(): string {
        return packageInfo.version;
    }

    getTermsDocumentUrl(): string {
        return GlobalsService.isCloud
            ? 'docs/terms-cloud.pdf'
            : 'docs/terms-on-premises.pdf';
    }

    openErrorDialog(err: IAgaveError, title: string = 'generic-error', force: boolean = false): Promise<any> {
        return GlobalsService.openErrorDialog(err, title, this._matDialog, force);
    }

    openInfoDialog(title: string, message: string): Promise<any> {
        return GlobalsService.openInfoDialog(title, message, this._matDialog);
    }

    openConfirmDialog(title: string, message: string): Promise<boolean> {
        return GlobalsService.openConfirmDialog({ title, message }, this._matDialog);
    }

    async openConfirmDialogData(data: ConfirmDialogData) {
        return await GlobalsService.openConfirmDialog(data, this._matDialog);
    }

    openErrorDialogString(errorDescription: string, title: string = 'generic-error', force: boolean = false): Promise<any> {
        return GlobalsService.openErrorDialogString(errorDescription, title, this._matDialog, force);
    }

    openSnackBar(message: string, action: string): void {
        GlobalsService.openSnackBar(this._matSnackBar, message, action);
    }

    exportExcel(page: string, params: any = {}, operators: any = {}, fileName: string = 'CadmoDriver.xls'): void {
        GlobalsService.exportExcel(page, this._agaveService.ticket, params, operators, fileName);
    }

    exportExcelProto(proto: Persistent, fileName: string = 'CadmoDriver.xls', action: string = 'export_excel'): void {
        const page = proto.getPage();
        const params = proto.getCadmoParams();
        const operators = proto.getCadmoOperators();
        GlobalsService.exportExcel(page, this._agaveService.ticket, params, operators, fileName, action);
    }
}
